import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { isValuePresent } from "../../../utils/builderUtils";
import froalaExtend from "packs/ctas/froala_extend.js";
import { froalaKey } from "src/froala_license";
import { createPortal } from "react-dom";
import detachedDisplayFeatureGate from "../../../utils/detachedDisplayFeatureGate";
import fixUnclosedHtmlTags from "../../../utils/fixUnclosedHtmlTags";

const DEFAULT_FONTS = {
  'Arial,Helvetica,sans-serif': 'Arial',
  'Georgia,serif': 'Georgia',
  "'Times New Roman',Times,serif": 'Times New Roman',
  'Verdana,Geneva,sans-serif': 'Verdana',
  "'Courier New',Courier,monospace": 'Courier New',
  "'Trebuchet MS',Helvetica,sans-serif": 'Trebuchet MS',
  'Impact,Charcoal,sans-serif': 'Impact',
};

const CFInput = ({
  type = "text",
  name,
  value: initialValue = "",
  placeholder,
  minLength,
  maxLength,
  onChange,
  onBlur,
  onFocus,
  alphanumeric,
  snakecase,
  numericWithPercentage,
  noCommas,
  wysiwyg = false,
  updateOnInputEnd = false,
  inputEndDelay = 1000,
  className = "",
  website
}) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [timeoutId, setTimeoutId] = useState(null);
  const inputRef = useRef(null);
  const froalaRef = useRef(null);
  const froalaEditorDiv = useRef(null);
  const froalaModalDiv = useRef(null);
  const codeContainerRef = useRef(null);
  const codeEditorRef = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);

  const openCodeModal = useCallback(() => {
    if (froalaRef.current) {
      const currentHtml = froalaRef.current.html.get(true);
      setValue(currentHtml || "");
    }

    setModalVisible(true);
  }, []);

  // Initialize Froala editor
  useEffect(() => {
    if (!wysiwyg || !froalaEditorDiv.current) return;

    const FroalaEditor = froalaExtend(website);
    let brand_color_vibrant = "#0072f7";
    let brand_color_dark = "#000";
    let brand_color_light = "#fff";

    if (website?.data) {
      brand_color_vibrant = website.data["brand_color_vibrant"] || brand_color_vibrant;
      brand_color_dark = website.data["brand_color_dark"] || brand_color_dark;
      brand_color_light = website.data["brand_color_light"] || brand_color_light;
    }

    const toolbarButtons = [
      "paragraphFormat", "insertLink", "bold", "italic", "fontFamily",
      "underline", "strikeThrough", "align", "fontSize", "lineHeight",
      "textColor", "backgroundColor", "customHTML"
    ];

    const color_codes = [
      '#FFFFFF', '#bdc3c7', '#7f8c8d', '#6C7A89',
      '#3498db', '#0075ff', '#2C82C9', '#475577',
      '#34495e', '#28324E', '#000000', '#4ECDC4',
      '#1abc9c', '#2ecc71', '#27ae60', '#16a085',
      '#019875', '#1E824C', '#F5D76E', '#FAC51C',
      '#FBA026', '#e67e22', '#E25041', '#B8312F',
      '#96281B', '#f624a2', '#BF55EC', '#9A12B3',
      '#663399', brand_color_light, brand_color_vibrant,
      brand_color_dark, 'REMOVE'
    ];

    const font_sizes = ['None', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '60', '72', '96'];
    const toolbar_buttons_img = ['filestackReplace', 'imageAlign', 'imageLink', 'linkOpen', 'linkEdit', 'linkRemove', 'imageStyle', 'imageSize', '-', 'imageRemove', 'imageAlt', 'imageDisplay'];

    const editor = froalaEditorDiv.current;
    editor.innerHTML = value || "";

    const froala = new FroalaEditor(editor, {
      attribution: false,
      toolbarButtons: {
        'moreText': { 'buttons': toolbarButtons, 'buttonsVisible': 2 },
        'misc': { 'buttons': ["filestack", "personalize", "clearFormatting"], 'buttonsVisible': 3 }
      },
      charCounterCount: !!maxLength,
      charCounterMax: maxLength || -1,
      keepFormatOnDelete: true,
      enter: FroalaEditor.ENTER_BR,
      fontSize: font_sizes,
      fontFamily: DEFAULT_FONTS,
      fontFamilySelection: true,
      imageEditButtons: toolbar_buttons_img,
      imageResize: false,
      colorsBackground: color_codes,
      colorsStep: 8,
      colorsText: color_codes,
      key: froalaKey(),
      linkStyles: { "cf-link-no-underline": "No underline", "cf-link-blue": "Blue hyperlink" },
      linkList: [],
      pastePlain: true,
      events: {
        'initialized': function() {
          $('.fr-element[contenteditable="true"]').attr("data-gramm", "false");
          $('.fr-command[data-cmd="fontFamily"]').on('click', function() {
            ctas.builder.fonts.froala_dropdown();
          });
        },
        'commands.before': function(cmd, e) {
          $(document).off('focusin.modal');
          if (cmd === "customHTML" && detachedDisplayFeatureGate(e, {}, "Custom HTML")) {
            this.events.trigger('blur', [], true);
            openCodeModal();
          }
          if (cmd === "filestack" || cmd === "filestackReplace") {
            froala.filestackUploading = true;
          }
        },
        'blur': function() {
          if ((froala.undo_index > 1 || froala.colorChanging) && !froala.filestackUploading) {
            froala.html.cleanEmptyTags();
            let html = froala.html.get(true);
            html = html.replace('id="selected"', "");
            if (html) {
              if (inputRef.current) inputRef.current.value = html;
              onBlur?.(html);
            }
          }
        }
      }
    });

    froalaRef.current = froala;

    return () => {
      if (froala && froala.destroy) froala.destroy();
    };
  }, [wysiwyg, website, openCodeModal, value]);

  const closeModal = useCallback(() => {
    if (codeEditorRef.current) {
      let newHtml = fixUnclosedHtmlTags(codeEditorRef.current.getValue());

      if (froalaRef.current) {
        froalaRef.current.html.set(newHtml);
      }

      if (inputRef.current) inputRef.current.value = newHtml;
      setValue(newHtml);
      onBlur?.(newHtml);
    }

    const modal = froalaModalDiv.current;
    if (modal) {
      $(modal).removeClass("in").css("display", "none");
      if ($(".modal.in").length < 1) {
        $("body").removeClass("modal-open");
      }
    }

    codeEditorRef.current = null;
    setModalVisible(false);
  }, [onBlur]);

  useEffect(() => {
    if (!modalVisible || !froalaModalDiv.current) return;

    const modal = froalaModalDiv.current;
    $(modal).modal("show");
    $(modal).addClass("in").css("display", "block");
    $("body").addClass("modal-open");
    $(modal).parents('.modal').scrollTop(0);

    const codeContainer = codeContainerRef.current;

    if (codeContainer) {
      codeContainer.innerHTML = "";
      const codeTextArea = document.createElement("textarea");
      codeTextArea.className = "scriptEditor";
      codeTextArea.value = value || "";
      codeContainer.appendChild(codeTextArea);

      const codeEditor = CodeMirror.fromTextArea(codeTextArea, {
        mode: "htmlmixed",
        theme: "material",
        lineWrapping: true,
        tabSize: 2,
      });

      codeEditorRef.current = codeEditor;
    }

    const handleClickOutside = (e) => {
      if (e.target === modal || $(e.target).parent()[0] === modal) {
        closeModal();
      }
    };

    modal.addEventListener("click", handleClickOutside);

    return () => {
      modal.removeEventListener("click", handleClickOutside);
    };
  }, [modalVisible, closeModal]);

  const formatSpacingValueInput = (val) => {
    if (val === null || val === undefined) return "";
    let formatted = val.replace(/[^0-9+%-.]+/gi, "");

    if (typeof formatted === 'string' && isValuePresent(formatted)) {
      formatted = String(parseFloat(formatted));
    }

    const percentIdx = val.indexOf('%');
    if (percentIdx !== -1 && percentIdx !== formatted.length - 1) {
      formatted = formatted.replace(/%/g, '') + '%';
    }

    const negIdx = formatted.indexOf('-');
    if (negIdx > 0) {
      formatted = '-' + formatted.replace(/-/g, '');
    }

    return formatted;
  };

  const formatValue = (inputValue) => {
    if (alphanumeric) return inputValue.replace(/[^a-z0-9+ _-]+/gi, "_");
    if (snakecase) return inputValue.replace(/[^a-z0-9+]+/gi, "_");
    if (numericWithPercentage) return formatSpacingValueInput(inputValue);
    if (noCommas) return inputValue.replace(/,/g, "");
    return inputValue;
  };

  const handleChange = (e) => {
    const formatted = formatValue(e.target.value);
    setValue(formatted);

    if (updateOnInputEnd) {
      clearTimeout(timeoutId);
      setTimeoutId(setTimeout(() => onChange?.(formatted), inputEndDelay));
    } else {
      onChange?.(formatted);
    }
  };

  const handleBlur = (e) => {
    const formatted = formatValue(e.target.value);
    setValue(formatted);
    onBlur?.(formatted);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const formatted = formatValue(e.target.value);
      setValue(formatted);
      onChange?.(formatted);
    }
  };

  const defaultClassName = "w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none";

  return (
    <React.Fragment>
      <input
        ref={inputRef}
        type={wysiwyg ? "hidden" : type}
        name={name}
        value={value}
        maxLength={maxLength}
        minLength={minLength}
        className={`${defaultClassName} ${className}`.trim()}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />

      {wysiwyg && (
        <>
          <div
            className="editor row hard-center"
            ref={froalaEditorDiv}
            dangerouslySetInnerHTML={{ __html: value || "" }}
          />

          {modalVisible && createPortal(
            <div
              className="modal in"
              ref={froalaModalDiv}
              tabIndex="-1"
              role="dialog"
              aria-labelledby="customHtmlModal"
              data-backdrop="false"
              style={{ background: "rgba(0, 0, 0, 0.21)", overflow: "visible", display: "block" }}
            >
              <div className="modal-dialog !mt-44 !w-full !h-full ml-auto mr-auto" role="document">
                <div className="modal-content hard-center !w-[900px] bg-white dark:bg-slate-950 !shadow-2xl">
                  <div className="modal-header border-slate-200 dark:border-slate-800">
                    <button
                      type="button"
                      className="close dark:text-white"
                      onClick={closeModal}
                      aria-label="Close"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <h4 className="text-2xl font-bold text-black dark:text-white" style={{ textTransform: "capitalize" }}>
                      Custom HTML
                    </h4>
                  </div>
                  <div className="modal-body">
                    <div className="row hard-center" style={{ maxWidth: "900px" }}>
                      <div data-name={name} className="step-script" ref={codeContainerRef} />
                      <p className="script-editor-note text-lg text-black dark:text-white smooth mt-5">
                        Javascript must have &lt;script&gt; tags. CSS must have &lt;style&gt; tags. Added scripts will run as asynchronous.
                      </p>
                    </div>
                  </div>
                  <div className="modal-footer !border-slate-200 dark:!border-slate-800">
                    <button
                      type="button"
                      className="btn bg-white border-solid border-[1px] rounded-md !border-slate-200 text-black text-xl hover:bg-blue-100 hover:!border-blue-100 hover:text-blue-600"
                      onClick={closeModal}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </>
      )}
    </React.Fragment>
  );
};

CFInput.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  alphanumeric: PropTypes.bool,
  snakecase: PropTypes.bool,
  numericWithPercentage: PropTypes.bool,
  noCommas: PropTypes.bool,
  updateOnInputEnd: PropTypes.bool,
  inputEndDelay: PropTypes.number,
  className: PropTypes.string,
  wysiwyg: PropTypes.bool,
  website: PropTypes.object,
};

export default CFInput;
